import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, Calendar, Clock, Users, Video, ExternalLink, XIcon, EditIcon, Trash2Icon } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { coffeeData, Coffee } from '../components/coffee/coffeeData';
import AlgaCoffeeSummaryWidget from '../components/coffee/widgets/AlgaCoffeeSummaryWidget';
import AlgaCoffeeTranscriptWidget from '../components/coffee/widgets/AlgaCoffeeTranscriptWidget';
import AlgaCoffeeNotesWidget from '../components/coffee/widgets/AlgaCoffeeNotesWidget';
import EditCoffeeModal from '../components/coffee/EditCoffeeModal';
const CoffeeDetailPage = () => {
  const {
    coffeeId
  } = useParams<{
    coffeeId: string;
  }>();
  const navigate = useNavigate();
  const [coffee, setCoffee] = useState<Coffee | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'info' | 'discussion' | 'participants'>('info');
  const [showEditModal, setShowEditModal] = useState(false);
  useEffect(() => {
    if (coffeeId) {
      const foundCoffee = coffeeData.find(c => c.id === coffeeId);
      if (foundCoffee) {
        setCoffee(foundCoffee);
      } else {
        // Coffee not found, redirect to coffee list
        navigate('/coffee');
      }
    }
  }, [coffeeId, navigate]);
  if (!coffee) {
    return <div className="flex h-screen bg-gray-100 items-center justify-center" data-id="element-1462">
        <p data-id="element-1463">Caricamento in corso...</p>
      </div>;
  }
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('it-IT', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const isAdmin = coffee.type === 'admin';
  const now = new Date();
  const coffeeDate = new Date(coffee.date);
  const isPast = coffeeDate < now;
  const isJoined = coffee.participants.some(p => p.id === 'current-user');
  const hasParticipated = isPast && isJoined;
  const isHost = coffee.host.id === 'current-user';
  // Platform logos
  const getPlatformLogo = (platform: string) => {
    switch (platform) {
      case 'Google Meet':
        return 'https://uploadthingy.s3.us-west-1.amazonaws.com/hawwkzWf3TnSE8Q31Hrx5c/images.png';
      case 'Microsoft Teams':
        return 'https://upload.wikimedia.org/wikipedia/commons/c/c9/Microsoft_Office_Teams_%282018%E2%80%93present%29.svg';
      case 'Zoom':
        return 'https://uploadthingy.s3.us-west-1.amazonaws.com/a6M2ckhEkmcVgkXUVkifgg/zoom_logo_icon_147196.png';
      default:
        return null;
    }
  };
  const handleUpdateCoffee = (updatedCoffee: Partial<Coffee>) => {
    if (coffee) {
      // In a real app, this would make an API call
      // For now, we'll just update the local state
      setCoffee({
        ...coffee,
        ...updatedCoffee
      });
      // Show a success message
      alert('Evento aggiornato con successo!');
    }
  };
  const handleDeleteCoffee = () => {
    // In a real app, this would make an API call
    // For now, we'll just redirect to the coffee list
    navigate('/coffee');
    // Show a success message
    alert('Evento eliminato con successo!');
  };
  const renderContent = () => {
    switch (activeTab) {
      case 'info':
        return <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-1464">
            <div className="md:col-span-3" data-id="element-1465">
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6" data-id="element-1466">
                <div className="p-6" data-id="element-1467">
                  <h2 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-1468">
                    Informazioni sull'evento
                  </h2>
                  <p className="text-gray-600 mb-6" data-id="element-1469">{coffee.description}</p>
                  <h3 className="text-md font-medium text-gray-800 mb-2" data-id="element-1470">
                    Temi trattati
                  </h3>
                  <div className="flex flex-wrap gap-2 mb-6" data-id="element-1471">
                    {coffee.topics.map((topic, index) => <span key={index} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm" data-id="element-1472">
                        {topic}
                      </span>)}
                  </div>
                  {isPast ? hasParticipated ? <div className="bg-custom-green/10 rounded-lg p-4 text-center" data-id="element-1473">
                        <p className="text-sm text-custom-green font-medium" data-id="element-1474">
                          Hai partecipato a questo evento
                        </p>
                      </div> : <div className="bg-gray-100 rounded-lg p-4 text-center" data-id="element-1475">
                        <p className="text-sm text-gray-500" data-id="element-1476">
                          Evento terminato
                        </p>
                      </div> : <button className="w-full flex items-center justify-center py-3 bg-custom-green text-white rounded-lg hover:bg-custom-green/90 transition-colors font-medium" data-id="element-1477">
                      <ExternalLink size={18} className="mr-2" data-id="element-1478" />
                      Partecipa all'evento
                    </button>}
                </div>
              </div>
              {hasParticipated && <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-1479">
                  <AlgaCoffeeSummaryWidget coffeeTitle={coffee.title} coffeeHost={coffee.host.name} coffeeTopics={coffee.topics} data-id="element-1480" />
                  <AlgaCoffeeNotesWidget coffeeHost={coffee.host.name} data-id="element-1481" />
                </div>}
            </div>
            <div className="md:col-span-1" data-id="element-1482">
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6" data-id="element-1483">
                <div className="p-4 border-b border-gray-200" data-id="element-1484">
                  <h2 className="text-base font-semibold text-gray-800" data-id="element-1485">
                    Dettagli dell'evento
                  </h2>
                </div>
                <div className="p-4" data-id="element-1486">
                  <div className="space-y-3" data-id="element-1487">
                    <div className="flex items-center text-gray-600" data-id="element-1488">
                      <Calendar size={18} className="mr-3 text-custom-green" data-id="element-1489" />
                      <span data-id="element-1490">{formatDate(coffee.date)}</span>
                    </div>
                    <div className="flex items-center text-gray-600" data-id="element-1491">
                      <Clock size={18} className="mr-3 text-custom-green" data-id="element-1492" />
                      <span data-id="element-1493">
                        {formatTime(coffee.date)} - {formatTime(coffee.endTime)}
                      </span>
                    </div>
                    <div className="flex items-center text-gray-600" data-id="element-1494">
                      {getPlatformLogo(coffee.platform) ? <img src={getPlatformLogo(coffee.platform)} alt={coffee.platform} className="w-5 h-5 mr-3" data-id="element-1495" /> : <Video size={18} className="mr-3 text-custom-green" data-id="element-1496" />}
                      <span data-id="element-1497">{coffee.platform}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6" data-id="element-1498">
                <div className="p-4 border-b border-gray-200" data-id="element-1499">
                  <h2 className="text-base font-semibold text-gray-800" data-id="element-1500">
                    Host
                  </h2>
                </div>
                <div className="p-4" data-id="element-1501">
                  <div className="flex items-center" data-id="element-1502">
                    <div className="w-12 h-12 rounded-full overflow-hidden mr-3" data-id="element-1503">
                      <img src={isAdmin ? 'https://uploadthingy.s3.us-west-1.amazonaws.com/nYvTviLNLZx36LeEKXMz6n/Frame_1.png' : coffee.host.avatar} alt={coffee.host.name} className="w-full h-full object-cover" data-id="element-1504" />
                    </div>
                    <div data-id="element-1505">
                      <p className="font-medium text-gray-800" data-id="element-1506">
                        {coffee.host.name}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-1507">
                        {isAdmin ? 'EIIS Team' : 'Community Member'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-1508">
                <div className="p-4 border-b border-gray-200" data-id="element-1509">
                  <h2 className="text-base font-semibold text-gray-800" data-id="element-1510">
                    Partecipanti
                  </h2>
                </div>
                <div className="p-4" data-id="element-1511">
                  <div className="flex items-center mb-3" data-id="element-1512">
                    <Users size={18} className="text-custom-green mr-2" data-id="element-1513" />
                    <span className="text-sm text-gray-500" data-id="element-1514">
                      {coffee.participants.length} partecipanti
                    </span>
                  </div>
                  <div className="space-y-3" data-id="element-1515">
                    {coffee.participants.map(participant => <div key={participant.id} className="flex items-center" data-id="element-1516">
                        <div className="w-8 h-8 rounded-full overflow-hidden mr-3" data-id="element-1517">
                          <img src={participant.avatar} alt={participant.name} className="w-full h-full object-cover" data-id="element-1518" />
                        </div>
                        <p className="text-sm text-gray-700" data-id="element-1519">
                          {participant.name}
                        </p>
                        {participant.id === 'current-user' && <span className="ml-auto text-xs bg-custom-green/10 text-custom-green px-2 py-0.5 rounded-full" data-id="element-1520">
                            Tu
                          </span>}
                      </div>)}
                  </div>
                </div>
              </div>
            </div>
          </div>;
      case 'discussion':
        return <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1521">
            <div className="md:col-span-2" data-id="element-1522">
              {hasParticipated ? <AlgaCoffeeTranscriptWidget coffeeHost={coffee.host.name} coffeeTitle={coffee.title} data-id="element-1523" /> : <div className="bg-white rounded-xl border border-gray-200 p-6 text-center" data-id="element-1524">
                  <p className="text-gray-500" data-id="element-1525">
                    {isPast ? 'Non hai partecipato a questo evento, quindi la trascrizione non è disponibile.' : "La trascrizione sarà disponibile dopo l'evento."}
                  </p>
                </div>}
            </div>
            <div className="md:col-span-1" data-id="element-1526">
              {hasParticipated && <AlgaCoffeeNotesWidget coffeeHost={coffee.host.name} data-id="element-1527" />}
            </div>
          </div>;
      case 'participants':
        return <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1528">
            <div className="md:col-span-2" data-id="element-1529">
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-1530">
                <div className="p-4 border-b border-gray-200" data-id="element-1531">
                  <h2 className="text-base font-semibold text-gray-800" data-id="element-1532">
                    Tutti i partecipanti
                  </h2>
                </div>
                <div className="p-6" data-id="element-1533">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1534">
                    {coffee.participants.map(participant => <div key={participant.id} className="flex items-center p-3 border border-gray-200 rounded-lg" data-id="element-1535">
                        <div className="w-10 h-10 rounded-full overflow-hidden mr-3" data-id="element-1536">
                          <img src={participant.avatar} alt={participant.name} className="w-full h-full object-cover" data-id="element-1537" />
                        </div>
                        <div data-id="element-1538">
                          <p className="font-medium text-gray-800" data-id="element-1539">
                            {participant.name}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-1540">
                            {participant.id === 'current-user' ? 'Tu' : 'Partecipante'}
                          </p>
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>
            </div>
            <div className="md:col-span-1" data-id="element-1541">
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-1542">
                <div className="p-4 border-b border-gray-200" data-id="element-1543">
                  <h2 className="text-base font-semibold text-gray-800" data-id="element-1544">
                    Informazioni sull'evento
                  </h2>
                </div>
                <div className="p-4" data-id="element-1545">
                  <div className="space-y-3" data-id="element-1546">
                    <div className="flex items-center text-gray-600" data-id="element-1547">
                      <Calendar size={18} className="mr-3 text-gray-500" data-id="element-1548" />
                      <span data-id="element-1549">{formatDate(coffee.date)}</span>
                    </div>
                    <div className="flex items-center text-gray-600" data-id="element-1550">
                      <Clock size={18} className="mr-3 text-gray-500" data-id="element-1551" />
                      <span data-id="element-1552">
                        {formatTime(coffee.date)} - {formatTime(coffee.endTime)}
                      </span>
                    </div>
                    <div className="flex items-center text-gray-600" data-id="element-1553">
                      {getPlatformLogo(coffee.platform) ? <img src={getPlatformLogo(coffee.platform)} alt={coffee.platform} className="w-5 h-5 mr-3" data-id="element-1554" /> : <Video size={18} className="mr-3 text-gray-500" data-id="element-1555" />}
                      <span data-id="element-1556">{coffee.platform}</span>
                    </div>
                    <div className="flex items-center text-gray-600" data-id="element-1557">
                      <Users size={18} className="mr-3 text-gray-500" data-id="element-1558" />
                      <span data-id="element-1559">{coffee.participants.length} partecipanti</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <div className="flex h-screen bg-gray-100" data-id="element-1560">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-40 md:hidden ${sidebarOpen ? 'block' : 'hidden'}`} data-id="element-1561">
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} data-id="element-1562"></div>
        <div className="relative flex-1 flex flex-col w-2/3 h-full bg-black" data-id="element-1563">
          <div className="absolute top-0 right-0 -mr-12 pt-2" data-id="element-1564">
            <button type="button" className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white" onClick={() => setSidebarOpen(false)} data-id="element-1565">
              <span className="sr-only" data-id="element-1566">Close sidebar</span>
              <XIcon className="h-6 w-6 text-white" aria-hidden="true" data-id="element-1567" />
            </button>
          </div>
          <div className="flex-1 h-full overflow-y-auto" data-id="element-1568">
            <Sidebar activePage="community" data-id="element-1569" />
          </div>
        </div>
      </div>
      {/* Static sidebar for desktop */}
      <div className="hidden md:flex md:flex-shrink-0" data-id="element-1570">
        <Sidebar activePage="community" data-id="element-1571" />
      </div>
      <div className="flex flex-col flex-1 overflow-hidden" data-id="element-1572">
        <Topbar onMenuClick={() => setSidebarOpen(true)} data-id="element-1573" />
        <main className="flex-1 overflow-y-auto p-4 md:p-6 pt-0 md:pt-0 bg-gray-100" data-id="element-1574">
          <div className="pt-4 md:pt-6 max-w-6xl mx-auto" data-id="element-1575">
            {/* Back button */}
            <div className="mb-6" data-id="element-1576">
              <button onClick={() => navigate('/coffee')} className="flex items-center text-gray-600 hover:text-gray-800" data-id="element-1577">
                <ArrowLeftIcon size={18} className="mr-2" data-id="element-1578" />
                <span data-id="element-1579">Torna a EIIS Coffee</span>
              </button>
            </div>
            {/* Coffee Header */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6 overflow-hidden" data-id="element-1580">
              <div className="p-6" data-id="element-1581">
                <div className="flex flex-col sm:flex-row items-start gap-4" data-id="element-1582">
                  <div className="w-full sm:w-[100px] h-[100px] rounded-full overflow-hidden flex-shrink-0 bg-gray-100 flex items-center justify-center border-2 border-gray-200" data-id="element-1583">
                    {isAdmin ? <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/nYvTviLNLZx36LeEKXMz6n/Frame_1.png" alt="EIIS Coffee" className="w-16 h-16 object-contain" data-id="element-1584" /> : <img src={coffee.host.avatar} alt={coffee.host.name} className="w-full h-full object-cover" data-id="element-1585" />}
                  </div>
                  <div className="flex-1" data-id="element-1586">
                    <div className="flex justify-between items-start" data-id="element-1587">
                      <h1 className="text-xl font-semibold text-gray-800 mb-2" data-id="element-1588">
                        {coffee.title}
                      </h1>
                      {isHost && !isPast && <div className="flex space-x-2" data-id="element-1589">
                          <button onClick={() => setShowEditModal(true)} className="flex items-center text-gray-600 hover:text-gray-800 px-2 py-1 rounded-md hover:bg-gray-100" data-id="element-1590">
                            <EditIcon size={16} className="mr-1" data-id="element-1591" />
                            <span className="text-sm" data-id="element-1592">Modifica</span>
                          </button>
                          <button onClick={() => {
                        if (window.confirm('Sei sicuro di voler eliminare questo evento?')) {
                          handleDeleteCoffee();
                        }
                      }} className="flex items-center text-red-600 hover:text-red-700 px-2 py-1 rounded-md hover:bg-red-50" data-id="element-1593">
                            <Trash2Icon size={16} className="mr-1" data-id="element-1594" />
                            <span className="text-sm" data-id="element-1595">Elimina</span>
                          </button>
                        </div>}
                    </div>
                    <p className="text-gray-600 mb-4 text-sm" data-id="element-1596">
                      Ospitato da {coffee.host.name} • {formatDate(coffee.date)}
                      {isHost && <span className="ml-2 text-blue-600 text-xs font-medium" data-id="element-1597">
                          (Sei l'host)
                        </span>}
                    </p>
                    <div className="flex flex-wrap items-center gap-4 sm:gap-6" data-id="element-1598">
                      <div className="flex items-center text-gray-500" data-id="element-1599">
                        <Clock size={16} className="mr-1.5" data-id="element-1600" />
                        <span className="text-sm" data-id="element-1601">
                          {formatTime(coffee.date)} -{' '}
                          {formatTime(coffee.endTime)}
                        </span>
                      </div>
                      <div className="flex items-center text-gray-500" data-id="element-1602">
                        <Users size={16} className="mr-1.5" data-id="element-1603" />
                        <span className="text-sm" data-id="element-1604">
                          {coffee.participants.length} partecipanti
                        </span>
                      </div>
                      <div className="flex items-center text-gray-500" data-id="element-1605">
                        {getPlatformLogo(coffee.platform) ? <img src={getPlatformLogo(coffee.platform)} alt={coffee.platform} className="w-4 h-4 mr-1.5" data-id="element-1606" /> : <Video size={16} className="mr-1.5" data-id="element-1607" />}
                        <span className="text-sm" data-id="element-1608">{coffee.platform}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Tab Navigation */}
              <div className="border-t border-gray-200 px-6 overflow-x-auto" data-id="element-1609">
                <nav className="flex space-x-8" data-id="element-1610">
                  <button onClick={() => setActiveTab('info')} className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'info' ? 'border-custom-green text-custom-green' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-1611">
                    Informazioni
                  </button>
                  <button onClick={() => setActiveTab('discussion')} className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'discussion' ? 'border-custom-green text-custom-green' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-1612">
                    Discussione
                  </button>
                  <button onClick={() => setActiveTab('participants')} className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'participants' ? 'border-custom-green text-custom-green' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-1613">
                    Partecipanti
                  </button>
                </nav>
              </div>
            </div>
            {/* Content area */}
            {renderContent()}
          </div>
        </main>
      </div>
      {/* Edit Coffee Modal */}
      {showEditModal && coffee && <EditCoffeeModal isOpen={showEditModal} onClose={() => setShowEditModal(false)} coffee={coffee} onUpdate={handleUpdateCoffee} onDelete={handleDeleteCoffee} data-id="element-1614" />}
    </div>;
};
export default CoffeeDetailPage;